import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { formatApiError } from '../../lib/errors';
import { Button } from '@/components/ui/button';
import { Gift, Users, Loader2, Trophy, Medal } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const MONTH_NAMES = {
  lv: ['', 'Janvāris', 'Februāris', 'Marts', 'Aprīlis', 'Maijs', 'Jūnijs', 'Jūlijs', 'Augusts', 'Septembris', 'Oktobris', 'Novembris', 'Decembris'],
  en: ['', 'January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'],
};

function StatCard({ label, value, subtext, accent = false }) {
  return (
    <div className={`p-4 rounded-2xl border ${accent ? 'border-[#D97736]/30 bg-[#FFF8F0]' : 'border-[#E5E4E0] bg-white'}`}>
      <div className="text-xs text-[#5C5C5C] mb-1">{label}</div>
      <div className={`font-outfit text-2xl font-bold ${accent ? 'text-[#D97736]' : 'text-[#121212]'}`}>{value}</div>
      {subtext && <div className="text-xs text-[#8A8A8A] mt-1">{subtext}</div>}
    </div>
  );
}

function ReferralsTab({ language }) {
  const isLV = language === 'lv';
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [topMetric, setTopMetric] = useState('bonus');

  useEffect(() => {
    (async () => {
      try {
        const { data } = await axios.get(`${API}/admin/referrals`, { params: { top_metric: topMetric } });
        setData(data);
      } catch (e) {
        console.error(e);
      } finally {
        setLoading(false);
      }
    })();
  }, [topMetric]);

  if (loading) {
    return (
      <div className="flex justify-center py-20">
        <Loader2 className="w-7 h-7 animate-spin text-[#D97736]" />
      </div>
    );
  }
  if (!data) {
    return <p className="text-center text-[#5C5C5C] py-12 text-sm">{isLV ? 'Nav datu' : 'No data'}</p>;
  }

  const eur = (n) => `€${(n ?? 0).toFixed(2)}`;
  const stats = data.stats || {};
  const referrers = data.referrers || [];
  const topMonthly = data.top_monthly || [];
  const monthLabel = (() => {
    if (!data.month) return '';
    const [y, m] = data.month.split('-').map(Number);
    const names = MONTH_NAMES[language] || MONTH_NAMES.lv;
    return `${names[m]} ${y}`;
  })();
  const RANK_STYLES = [
    { bg: 'bg-[#FEF3C7]', ring: 'ring-[#F59E0B]', text: 'text-[#B45309]' },
    { bg: 'bg-[#F1F5F9]', ring: 'ring-[#94A3B8]', text: 'text-[#475569]' },
    { bg: 'bg-[#FDEAD7]', ring: 'ring-[#D97736]', text: 'text-[#B45309]' },
  ];

  return (
    <div className="space-y-6" data-testid="admin-referrals-tab">
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <StatCard
          label={isLV ? 'KOPĀ IZMAKSĀTS BONUSS' : 'TOTAL BONUS PAID'}
          value={eur(stats.total_bonus)}
          subtext={isLV ? 'visi laiki · sedz KoLV' : 'all-time · paid by KoLV'}
          accent
        />
        <StatCard
          label={isLV ? 'SAITES ATVĒRUMI' : 'LINK OPENS'}
          value={stats.total_visits ?? 0}
          subtext={isLV ? `konversija ${(stats.total_conversion ?? 0).toFixed(1)}%` : `${(stats.total_conversion ?? 0).toFixed(1)}% conversion`}
        />
        <StatCard
          label={isLV ? 'AKTĪVIE UZAICINĀTĀJI' : 'ACTIVE REFERRERS'}
          value={stats.total_referrers ?? 0}
        />
        <StatCard
          label={isLV ? 'UZAICINĀTIE LIETOTĀJI' : 'INVITED USERS'}
          value={stats.total_invited ?? 0}
        />
      </div>

      {/* Monthly TOP referrers */}
      <div className="bg-gradient-to-br from-[#FFF8F0] to-white border border-[#D97736]/20 rounded-2xl p-5" data-testid="referrals-top-monthly">
        <div className="flex items-center justify-between gap-3 flex-wrap mb-4">
          <h3 className="font-outfit font-semibold text-[#121212] flex items-center gap-2">
            <Trophy className="w-5 h-5 text-[#F59E0B]" />
            {isLV ? 'TOP uzaicinātāji' : 'TOP referrers'}
            {monthLabel && <span className="text-sm font-normal text-[#8A8A8A]">· {monthLabel}</span>}
          </h3>
          <div className="flex items-center gap-1.5" data-testid="top-metric-picker">
            {[
              { k: 'bonus', lv: 'Bonuss', en: 'Bonus' },
              { k: 'signups', lv: 'Reģistrācijas', en: 'Signups' },
              { k: 'sales', lv: 'Pārdevumi', en: 'Sales' },
            ].map((o) => (
              <button
                key={o.k}
                onClick={() => setTopMetric(o.k)}
                className={`h-8 px-3 rounded-lg border text-xs font-medium transition-colors ${topMetric === o.k ? 'border-[#D97736] bg-[#D97736] text-white' : 'border-[#E5E4E0] bg-white text-[#5C5C5C] hover:border-[#C9C8C4]'}`}
                data-testid={`top-metric-${o.k}`}
              >
                {isLV ? o.lv : o.en}
              </button>
            ))}
          </div>
        </div>
        {topMonthly.length === 0 ? (
          <p className="text-sm text-[#5C5C5C] py-6 text-center">
            {isLV ? 'Šomēnes vēl nav aktivitātes' : 'No activity this month yet'}
          </p>
        ) : (
          <div className="space-y-2">
            {topMonthly.map((r, i) => {
              const rs = RANK_STYLES[i] || null;
              const metric = data.top_metric || 'bonus';
              const primary = metric === 'bonus' ? eur(r.bonus)
                : metric === 'signups' ? `${r.signups} ${isLV ? 'reģ.' : 'signups'}`
                : `${r.sales} ${isLV ? 'pārd.' : 'sales'}`;
              const subParts = [
                metric !== 'bonus' ? eur(r.bonus) : null,
                metric !== 'signups' ? `${r.signups} ${isLV ? 'reģ.' : 'signups'}` : null,
                metric !== 'sales' ? `${r.sales} ${isLV ? 'pārd.' : 'sales'}` : null,
              ].filter(Boolean);
              return (
                <div key={r.referrer_id} className="flex items-center gap-3 bg-white border border-[#F0EFEB] rounded-xl px-3 py-2.5" data-testid={`top-referrer-${i}`}>
                  <div className={`w-8 h-8 shrink-0 rounded-full flex items-center justify-center font-bold text-sm ${rs ? `${rs.bg} ${rs.text} ring-1 ${rs.ring}` : 'bg-[#F5F5F3] text-[#8A8A8A]'}`}>
                    {i < 3 ? <Medal className="w-4 h-4" /> : i + 1}
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="font-medium text-[#121212] truncate">{r.name || '—'}</div>
                    <div className="text-xs text-[#8A8A8A] truncate">{r.email}</div>
                  </div>
                  <div className="text-right shrink-0">
                    <div className="font-outfit font-bold text-[#D97736] tabular-nums">{primary}</div>
                    <div className="text-xs text-[#8A8A8A]">{subParts.join(' · ')}</div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      <div className="bg-white border border-[#E5E4E0] rounded-2xl p-5">
        <div className="flex items-center justify-between gap-3 flex-wrap mb-4">
          <h3 className="font-outfit font-semibold text-[#121212] flex items-center gap-2">
            <Gift className="w-5 h-5 text-[#D97736]" />
            {isLV ? 'Uzaicinātāji' : 'Referrers'} ({referrers.length})
          </h3>
          <ReferralExportControls language={language} />
        </div>
        {referrers.length === 0 ? (
          <p className="text-sm text-[#5C5C5C] py-8 text-center flex flex-col items-center gap-2">
            <Users className="w-6 h-6 text-[#D1D0CC]" />
            {isLV ? 'Vēl neviens nav uzaicinājis lietotājus' : 'Nobody has invited users yet'}
          </p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="referrals-table">
              <thead>
                <tr className="border-b border-[#E5E4E0] text-xs text-[#8A8A8A] uppercase">
                  <th className="text-left py-2 pr-3">{isLV ? 'Uzaicinātājs' : 'Referrer'}</th>
                  <th className="text-right py-2 pr-3">{isLV ? 'Atvērumi' : 'Opens'}</th>
                  <th className="text-right py-2 pr-3">{isLV ? 'Reģistr.' : 'Signups'}</th>
                  <th className="text-right py-2 pr-3">{isLV ? 'Konversija' : 'Conversion'}</th>
                  <th className="text-right py-2 pr-3">{isLV ? 'Pārdevumi' : 'Sales'}</th>
                  <th className="text-right py-2 pr-3">{isLV ? 'Apgrozījums' : 'Gross'}</th>
                  <th className="text-right py-2">{isLV ? 'Nopelnīts' : 'Earned'}</th>
                </tr>
              </thead>
              <tbody>
                {referrers.map((r) => (
                  <tr key={r.referrer_id} className="border-b border-[#F0EFEB] hover:bg-[#FAFAFA]" data-testid={`referral-row-${r.referrer_id}`}>
                    <td className="py-2.5 pr-3">
                      <div className="font-medium text-[#121212] truncate max-w-[220px]">{r.name || '—'}</div>
                      <div className="text-xs text-[#8A8A8A] truncate max-w-[220px]">{r.email}</div>
                    </td>
                    <td className="py-2.5 pr-3 text-right text-[#5C5C5C] tabular-nums" data-testid={`referral-visits-${r.referrer_id}`}>{r.visits ?? 0}</td>
                    <td className="py-2.5 pr-3 text-right text-[#5C5C5C] tabular-nums">{r.invited}</td>
                    <td className="py-2.5 pr-3 text-right tabular-nums" data-testid={`referral-conversion-${r.referrer_id}`}>
                      <span className={`font-medium ${(r.conversion ?? 0) >= 50 ? 'text-[#16A34A]' : (r.conversion ?? 0) > 0 ? 'text-[#D97736]' : 'text-[#B8B8B8]'}`}>
                        {(r.conversion ?? 0).toFixed(1)}%
                      </span>
                    </td>
                    <td className="py-2.5 pr-3 text-right text-[#5C5C5C] tabular-nums">{r.sales}</td>
                    <td className="py-2.5 pr-3 text-right text-[#5C5C5C] tabular-nums">{eur(r.gross)}</td>
                    <td className="py-2.5 text-right font-semibold text-[#D97736] tabular-nums">{eur(r.bonus)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

function ReferralExportControls({ language }) {
  const isLV = language === 'lv';
  const now = new Date();
  const currentYear = now.getFullYear();
  const [year, setYear] = useState(String(currentYear));
  const [month, setMonth] = useState('all');
  const [downloading, setDownloading] = useState(false);

  const years = [];
  for (let y = currentYear; y >= currentYear - 4; y--) years.push(y);

  const download = async () => {
    setDownloading(true);
    try {
      const params = {};
      if (year !== 'all') params.year = year;
      if (year !== 'all' && month !== 'all') params.month = month;
      const res = await axios.get(`${API}/admin/referrals/export`, {
        params,
        responseType: 'blob',
      });
      const cd = res.headers['content-disposition'] || '';
      const match = /filename="([^"]+)"/.exec(cd);
      const filename = match ? match[1] : 'kolv-referrals.csv';
      const url = URL.createObjectURL(new Blob([res.data], { type: 'text/csv' }));
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(url);
    } catch (e) {
      alert(formatApiError(e, isLV ? 'Lejupielāde neizdevās' : 'Download failed'));
    } finally {
      setDownloading(false);
    }
  };

  const monthLabels = MONTH_NAMES[language] || MONTH_NAMES.lv;

  return (
    <div className="flex items-center gap-2 flex-wrap" data-testid="referral-export-controls">
      <select
        value={year}
        onChange={(e) => setYear(e.target.value)}
        className="text-sm px-3 py-1.5 rounded-lg border border-[#E5E4E0] bg-white"
        data-testid="referral-export-year"
      >
        <option value="all">{isLV ? 'Visi gadi' : 'All years'}</option>
        {years.map((y) => <option key={y} value={y}>{y}</option>)}
      </select>
      <select
        value={month}
        onChange={(e) => setMonth(e.target.value)}
        disabled={year === 'all'}
        className="text-sm px-3 py-1.5 rounded-lg border border-[#E5E4E0] bg-white disabled:opacity-50"
        data-testid="referral-export-month"
      >
        <option value="all">{isLV ? 'Visi mēneši' : 'All months'}</option>
        {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12].map((m) => (
          <option key={m} value={m}>{monthLabels[m]}</option>
        ))}
      </select>
      <Button
        size="sm"
        onClick={download}
        disabled={downloading}
        className="bg-[#D97736] hover:bg-[#C06528] text-white"
        data-testid="referral-export-btn"
      >
        {downloading
          ? (isLV ? 'Lejupielādē...' : 'Downloading...')
          : (isLV ? 'Lejupielādēt CSV' : 'Download CSV')}
      </Button>
    </div>
  );
}

export default ReferralsTab;
